import React, { useState, useRef, useEffect } from 'react'
import {
  Segment,
  Input,
  Button,
  Message,
  Icon,
  List,
  Header,
  Loader,
  Label,
  Divider,
} from 'semantic-ui-react'
import useAxios from '../../lib/auth/useAxios'
import './ProductAgentChat.css'

const ProductAgentChat = ({ onProductsCreated }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi! I'm your AI product management assistant. I can help you create products, add attributes, and copy configurations. Try saying something like:\n\n• 'Add dimensional lumber for DF with sizes 2x4 to 2x12'\n• 'Copy all attributes from Douglas Fir to Hem-Fir'\n• 'Create timbers products for common species'",
    },
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const messagesEndRef = useRef(null)
  const axios = useAxios()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = async (userMessage) => {
    if (!userMessage.trim()) return

    setLoading(true)

    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }]
    setMessages(newMessages)
    setInput('')

    try {
      const response = await axios.post('/main/product-agent/chat/', {
        message: userMessage,
        conversation_history: messages.filter((m) => m.role !== 'system'),
      })

      const data = response.data

      // Handle different response types
      if (data.type === 'clarification') {
        // AI needs more info
        setMessages([
          ...newMessages,
          {
            role: 'assistant',
            content: data.assistant_message,
            questions: data.questions,
            understood: data.understood,
          },
        ])
      } else if (data.type === 'action') {
        // AI wants to do something - ask for confirmation
        setPendingAction(data)
        setMessages([
          ...newMessages,
          {
            role: 'assistant',
            content: data.preview_message,
            requiresConfirmation: true,
            preview: data.function_args,
            action: data.action,
          },
        ])
      } else {
        // Just a conversational message
        setMessages([
          ...newMessages,
          { role: 'assistant', content: data.message || data.assistant_message },
        ])
      }
    } catch (error) {
      console.error('Agent error:', error)
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content:
            'Sorry, I encountered an error: ' +
            (error.response?.data?.error || error.message),
          error: true,
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const confirmAction = async () => {
    if (!pendingAction) return

    setLoading(true)
    try {
      const response = await axios.post('/main/product-agent/confirm/', {
        action: pendingAction.action,
        function_args: pendingAction.function_args,
      })

      const newMessage = {
        role: 'assistant',
        content: response.data.message,
        success: response.data.success,
        results: response.data.results,
      }

      setMessages([...messages, newMessage])
      setPendingAction(null)

      // Notify parent to reload products
      if (onProductsCreated && response.data.success) {
        setTimeout(() => onProductsCreated(), 500)
      }
    } catch (error) {
      console.error('Confirmation error:', error)
      setMessages([
        ...messages,
        {
          role: 'assistant',
          content:
            'Error executing action: ' +
            (error.response?.data?.error || error.message),
          error: true,
        },
      ])
      setPendingAction(null)
    } finally {
      setLoading(false)
    }
  }

  const cancelAction = () => {
    setPendingAction(null)
    setMessages([
      ...messages,
      { role: 'assistant', content: 'Action cancelled. How else can I help?' },
    ])
  }

  const renderPreview = (preview, action) => {
    if (!preview) return null

    if (action === 'create_products') {
      return (
        <Segment className="agent-preview">
          <Header size="tiny">Products to create:</Header>
          {preview.products?.map((product, idx) => (
            <div key={idx} style={{ marginBottom: '10px' }}>
              <strong>{product.name}</strong>
              {product.commodity && (
                <Label size="mini" style={{ marginLeft: '5px' }}>
                  {product.commodity}
                </Label>
              )}
              {product.commodity_form && (
                <Label size="mini" color="blue">
                  {product.commodity_form}
                </Label>
              )}
              {product.attributes && (
                <List bulleted style={{ marginTop: '5px', fontSize: '0.9em' }}>
                  {product.attributes.map((attr, ai) => (
                    <List.Item key={ai}>
                      <strong>{attr.name}</strong>: {attr.selections?.length || 0}{' '}
                      selections
                      {attr.is_primary && (
                        <Label size="mini" color="purple" style={{ marginLeft: '5px' }}>
                          Primary
                        </Label>
                      )}
                      {attr.has_price && (
                        <Label size="mini" color="green" style={{ marginLeft: '5px' }}>
                          Affects Price
                        </Label>
                      )}
                    </List.Item>
                  ))}
                </List>
              )}
            </div>
          ))}
        </Segment>
      )
    }

    if (action === 'copy_attributes') {
      return (
        <Segment className="agent-preview">
          <Header size="tiny">Copy operation:</Header>
          <p>
            <strong>From:</strong> {preview.from_product_name}
            <br />
            <strong>To:</strong> {preview.to_product_names?.join(', ')}
            {preview.attribute_names && (
              <>
                <br />
                <strong>Attributes:</strong> {preview.attribute_names.join(', ')}
              </>
            )}
          </p>
        </Segment>
      )
    }

    return (
      <Segment className="agent-preview">
        <pre style={{ fontSize: '0.85em', overflow: 'auto' }}>
          {JSON.stringify(preview, null, 2)}
        </pre>
      </Segment>
    )
  }

  const renderResults = (results) => {
    if (!results) return null

    return (
      <Segment className="agent-results">
        {results.created_products?.length > 0 && (
          <div>
            <Label color="green" size="small">
              <Icon name="check circle" />
              Created {results.created_products.length} product(s)
            </Label>
          </div>
        )}
        {results.created_attributes?.length > 0 && (
          <div style={{ marginTop: '5px' }}>
            <Label color="blue" size="small">
              <Icon name="tags" />
              Created {results.created_attributes.length} attribute(s)
            </Label>
          </div>
        )}
        {results.created_selections?.length > 0 && (
          <div style={{ marginTop: '5px' }}>
            <Label color="teal" size="small">
              <Icon name="list" />
              Created {results.created_selections.length} selection(s)
            </Label>
          </div>
        )}
        {results.copied_to?.length > 0 && (
          <div style={{ marginTop: '5px' }}>
            <Label color="purple" size="small">
              <Icon name="copy" />
              Copied to {results.copied_to.length} product(s)
            </Label>
          </div>
        )}
        {results.errors?.length > 0 && (
          <div style={{ marginTop: '5px' }}>
            <Message negative size="small">
              <Message.Header>Errors:</Message.Header>
              <List bulleted>
                {results.errors.map((err, idx) => (
                  <List.Item key={idx}>{err}</List.Item>
                ))}
              </List>
            </Message>
          </div>
        )}
      </Segment>
    )
  }

  return (
    <div className="product-agent-chat">
      <div className="agent-messages">
        {messages.map((msg, idx) => (
          <Message
            key={idx}
            className={`agent-message ${msg.role}`}
            color={
              msg.role === 'user'
                ? 'blue'
                : msg.error
                ? 'red'
                : msg.success
                ? 'green'
                : undefined
            }
          >
            <Message.Header>
              <Icon name={msg.role === 'user' ? 'user' : 'robot'} />
              {msg.role === 'user' ? 'You' : 'AI Assistant'}
            </Message.Header>
            <Message.Content>
              <p style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</p>

              {msg.understood && (
                <Segment size="small" style={{ marginTop: '10px' }}>
                  <Header size="tiny">What I understood:</Header>
                  <p>{msg.understood}</p>
                </Segment>
              )}

              {msg.questions && msg.questions.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <Header size="tiny">I need clarification on:</Header>
                  <List ordered>
                    {msg.questions.map((q, i) => (
                      <List.Item key={i}>{q}</List.Item>
                    ))}
                  </List>
                </div>
              )}

              {msg.requiresConfirmation && renderPreview(msg.preview, msg.action)}

              {msg.requiresConfirmation && (
                <div style={{ marginTop: '15px' }}>
                  <Button
                    color="green"
                    onClick={confirmAction}
                    loading={loading}
                    disabled={loading}
                  >
                    <Icon name="check" />
                    Yes, proceed
                  </Button>
                  <Button onClick={cancelAction} disabled={loading}>
                    <Icon name="cancel" />
                    Cancel
                  </Button>
                </div>
              )}

              {msg.results && renderResults(msg.results)}
            </Message.Content>
          </Message>
        ))}

        {loading && !pendingAction && (
          <Message icon className="agent-message assistant">
            <Icon name="circle notched" loading />
            <Message.Content>
              <Message.Header>AI Assistant</Message.Header>
              Thinking...
            </Message.Content>
          </Message>
        )}

        <div ref={messagesEndRef} />
      </div>

      <Divider />

      <div className="agent-input">
        <Input
          fluid
          placeholder="Type your request... (e.g., 'Add dimensional lumber for DF and HF with common sizes')"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage(input)}
          disabled={loading || pendingAction}
          action={
            <Button
              color="blue"
              onClick={() => sendMessage(input)}
              loading={loading}
              disabled={!input.trim() || loading || pendingAction}
            >
              <Icon name="send" />
              Send
            </Button>
          }
        />
      </div>
    </div>
  )
}

export default ProductAgentChat
